import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { START_COMPONENT_NAME } from '@assetaccountant/core/models/onboarding/component-names';
import { OrganisationOnboardingModel } from '@assetaccountant/core/models/onboarding/onboarding-model';
import { OnboardingOrganisationRequirements } from '@assetaccountant/core/models/onboarding/onboarding-organisation-requirements';
import { StartStatus } from '@assetaccountant/core/models/onboarding/start-status';
import { Register } from '@assetaccountant/core/models/register';
import { RegisterIntegration } from '@assetaccountant/core/models/register-integration';
import { OrganisationResolverResult } from '@assetaccountant/core/resolvers/organisation.resolver';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { OnboardingService } from '@assetaccountant/core/services/onboarding.service';
import { OrganisationService } from '@assetaccountant/core/services/organisation.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { combineLatest, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { OnboardingQuestionsComponent } from './onboarding-questions/onboarding-questions.component';

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  standalone: false,
})
export class StartComponent implements OnInit, OnDestroy {
  @ViewChild(OnboardingQuestionsComponent)
  onboardingQuestionsComponent: OnboardingQuestionsComponent;

  readonly OnboardingOrganisationRequirements = OnboardingOrganisationRequirements;
  readonly StartStatus = StartStatus;

  onboarding?: OrganisationOnboardingModel;
  organisation: OrganisationResolverResult;
  primaryIntegration?: RegisterIntegration;
  register: Register;
  registers: Register[];

  getRegisters$ = this.organisationService
    .listRegisters()
    .pipe(take(1))
    .pipe(tap(({ registers }) => (this.registers = registers)));

  get demoRegister() {
    return this.registers?.find(r => r.isDemo);
  }

  get hasDemoRegister() {
    return this.registers?.some(r => r.isDemo);
  }

  status: StartStatus = StartStatus.Loading;

  get title() {
    return {
      componentName: START_COMPONENT_NAME,
      pageTitle: 'Welcome to FinQuery Fixed Assets',
      pageHeading: [{ name: 'Welcome to FinQuery Fixed Assets' }],
    };
  }

  private subscriptions: Subscription[] = [
    this.layoutService.demoRegisterCreated.subscribe(() => this.getRegisters$.subscribe()),
  ];

  constructor(
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    private organisationService: OrganisationService,
    private appNavigationService: AppNavigationService,
    private router: Router,
    private onboardingService: OnboardingService,
    private trackingService: TrackingService,
    private layoutService: LayoutService,
  ) {}

  ngOnInit() {
    this.router.events.subscribe(routeEvent => {
      if (routeEvent instanceof NavigationEnd) {
        this.onboardingService.resetOrganisation();
        this.onboardingQuestionsComponent?.reset();
      }
    });

    this.subscriptions.push(
      combineLatest([this.onboardingService.getOnboarding(), this.route.data, this.getRegisters$])
        .pipe(take(1))
        .subscribe(([onboarding, data]) => {
          const { register, organisation } = data;
          this.register = register;
          this.organisation = organisation;
          this.primaryIntegration = register?.integrations.find(i => i.isPrimary);
          this.onboarding = onboarding;

          this.subscriptions.push(
            this.organisationService
              .recordAccess(this.organisation.activeOrganisation.organisationId)
              .subscribe({ error: () => {} }),
          );

          if (!onboarding && this.route.snapshot.queryParams['action'] !== 'trial') {
            this.status = StartStatus.Questions;
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { action: 'trial' },
              queryParamsHandling: 'merge',
            });
          } else if (!onboarding && this.route.snapshot.queryParams['action'] === 'trial') {
            this.status = StartStatus.Questions;
          } else {
            this.onGoToDemoRegister();
          }
        }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onGoToDemoRegister() {
    if (this.demoRegister) {
      // Go to the demo register
      this.appNavigationService.switchRegister(this.demoRegister);
    } else if (this.organisation.activeOrganisation && this.registers.length) {
      // If there is no demo register the user came from an app store, so go to the integration settings
      this.router.navigate(
        [
          '/organisations',
          this.organisation.activeOrganisation.organisationId,
          'registers',
          this.registers[0].id,
          'settings',
        ],
        { fragment: 'integrations' },
      );
    }
  }

  questionsComplete() {
    this.onboardingService.resetOrganisation();
    this.onboardingService.getOnboarding().subscribe(result => {
      this.onboarding = result;
    });

    this.onGoToDemoRegister();
  }
}
